import React, { useEffect, useRef } from 'react';
import { useForm, ValidationError } from '@formspree/react';
import SEO from '../components/SEO';

const fields = ['name', 'email', 'message'];

const Contact = () => {
  const [state, handleSubmit] = useForm('xdenywdn');
  const fieldRefs = useRef({});

  useEffect(() => {
    if (!state.errors?.length) {
      return;
    }

    const firstInvalidField = fields.find((field) =>
      state.errors.some((error) => error.field === field),
    );

    if (firstInvalidField) {
      fieldRefs.current[firstInvalidField]?.focus();
    }
  }, [state.errors]);

  if (state.succeeded) {
    return (
      <section
        aria-labelledby="message-sent-title"
        className="mx-auto max-w-3xl px-6 py-32"
      >
        <SEO
          title="Message Sent"
          description="Your message to Broadway Muses has been sent successfully."
          path="/contact"
        />
        <div
          role="status"
          aria-live="polite"
          className="border-4 border-black p-8 text-center md:p-16"
        >
          <p className="mb-6 font-mono text-xs font-black uppercase tracking-[0.3em]">
            Curtain Call
          </p>
          <h1
            id="message-sent-title"
            className="font-serif text-5xl font-black uppercase italic md:text-7xl"
          >
            Thank You
          </h1>
          <p className="mt-8 font-mono text-sm uppercase leading-loose">
            Your message has been sent. We will be in touch soon.
          </p>
        </div>
      </section>
    );
  }

  const hasErrors = Boolean(state.errors?.length);
  const hasFieldError = (field) =>
    Boolean(state.errors?.some((error) => error.field === field));

  return (
    <section
      aria-labelledby="contact-title"
      className="mx-auto w-full max-w-3xl px-6 py-32"
    >
      <SEO
        title="Contact"
        description="Contact Broadway Muses with questions, feedback, or a message about Broadway."
        path="/contact"
      />

      <header className="mb-16 border-b-8 border-black pb-6">
        <p className="mb-6 font-mono text-xs font-black uppercase tracking-[0.3em]">
          Broadway Muses
        </p>
        <h1
          id="contact-title"
          className="break-words font-serif text-6xl font-black uppercase italic md:text-8xl"
        >
          Contact
        </h1>
      </header>

      <p
        id="contact-instructions"
        className="mb-10 font-mono text-sm leading-loose"
      >
        Send Broadway Muses a message using the form below. Required fields are
        marked with an asterisk.
      </p>

      {hasErrors && (
        <div
          id="contact-form-errors"
          role="alert"
          tabIndex="-1"
          className="mb-8 border-2 border-red-700 p-4 font-mono text-xs font-bold uppercase leading-loose text-red-700"
        >
          There was a problem sending your message. Review the highlighted
          fields, correct any errors, and try again.
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className="space-y-8"
        noValidate
        aria-describedby="contact-instructions"
      >
        <div>
          <label
            htmlFor="name"
            className="mb-3 block font-mono text-xs font-black uppercase"
          >
            Name <span aria-hidden="true">*</span>
            <span className="sr-only">, required</span>
          </label>
          <input
            ref={(element) => {
              fieldRefs.current.name = element;
            }}
            id="name"
            name="name"
            type="text"
            required
            autoComplete="name"
            aria-required="true"
            aria-invalid={hasFieldError('name')}
            aria-describedby="name-error"
            className="w-full border-2 border-black bg-white px-4 py-4 font-mono text-sm outline-none focus:bg-black focus:text-white focus-visible:outline-white"
          />
          <span
            id="name-error"
            className="font-mono text-xs font-bold text-red-700"
          >
            <ValidationError field="name" prefix="Name" errors={state.errors} />
          </span>
        </div>

        <div>
          <label
            htmlFor="email"
            className="mb-3 block font-mono text-xs font-black uppercase"
          >
            Email <span aria-hidden="true">*</span>
            <span className="sr-only">, required</span>
          </label>
          <input
            ref={(element) => {
              fieldRefs.current.email = element;
            }}
            id="email"
            name="email"
            type="email"
            required
            autoComplete="email"
            inputMode="email"
            aria-required="true"
            aria-invalid={hasFieldError('email')}
            aria-describedby="email-error"
            className="w-full border-2 border-black bg-white px-4 py-4 font-mono text-sm outline-none focus:bg-black focus:text-white focus-visible:outline-white"
          />
          <span
            id="email-error"
            className="font-mono text-xs font-bold text-red-700"
          >
            <ValidationError
              field="email"
              prefix="Email"
              errors={state.errors}
            />
          </span>
        </div>

        <div>
          <label
            htmlFor="message"
            className="mb-3 block font-mono text-xs font-black uppercase"
          >
            Message <span aria-hidden="true">*</span>
            <span className="sr-only">, required</span>
          </label>
          <textarea
            ref={(element) => {
              fieldRefs.current.message = element;
            }}
            id="message"
            name="message"
            rows="7"
            required
            aria-required="true"
            aria-invalid={hasFieldError('message')}
            aria-describedby="message-error"
            className="w-full resize-y border-2 border-black bg-white px-4 py-4 font-mono text-sm outline-none focus:bg-black focus:text-white focus-visible:outline-white"
          />
          <span
            id="message-error"
            className="font-mono text-xs font-bold text-red-700"
          >
            <ValidationError
              field="message"
              prefix="Message"
              errors={state.errors}
            />
          </span>
        </div>

        <input
          id="website"
          name="_gotcha"
          type="text"
          aria-hidden="true"
          tabIndex="-1"
          autoComplete="off"
          style={{ position: 'absolute', left: '-9999px' }}
        />

        <button
          type="submit"
          disabled={state.submitting}
          className="min-h-12 border-2 border-black bg-black px-8 py-4 font-mono text-xs font-black uppercase tracking-[0.2em] text-white transition-colors hover:bg-white hover:text-black focus-visible:outline-white disabled:cursor-not-allowed disabled:opacity-50"
        >
          {state.submitting ? 'Sending message' : 'Send message'}
        </button>
      </form>
    </section>
  );
};

export default Contact;